import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'gatsby';
import styled from 'styled-components';
import { navLinks } from '@config';
import { KEY_CODES } from '@utils';
import { useOnClickOutside } from '@hooks';

const StyledMenu = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: block;
  }
`;

const StyledHamburgerButton = styled.button`
  display: none;

  @media (max-width: 768px) {
    ${({ theme }) => theme.mixins.flexCenter};
    position: relative;
    z-index: 20;
    margin-right: -12px;
    padding: 12px;
    border: 0;
    background: transparent;
    color: var(--text-primary);
    text-transform: none;
    transition: var(--transition);
  }

  .ham-box {
    display: inline-block;
    position: relative;
    width: var(--hamburger-width);
    height: 24px;
  }

  .ham-box-inner {
    position: absolute;
    top: 50%;
    right: 0;
    width: var(--hamburger-width);
    height: 2px;
    border-radius: 999px;
    background-color: var(--text-primary);
    transition-duration: 0.22s;
    transition-property: transform;
    transition-delay: ${props => (props.menuOpen ? `0.12s` : `0s`)};
    transform: rotate(${props => (props.menuOpen ? `225deg` : `0deg`)});
    transition-timing-function: cubic-bezier(
      ${props => (props.menuOpen ? `0.215, 0.61, 0.355, 1` : `0.55, 0.055, 0.675, 0.19`)}
    );

    &:before,
    &:after {
      content: '';
      display: block;
      position: absolute;
      right: 0;
      width: var(--hamburger-width);
      height: 2px;
      border-radius: 999px;
      background-color: var(--text-primary);
      transition-timing-function: ease;
      transition-duration: 0.15s;
      transition-property: transform, top, bottom, opacity, width;
    }

    &:before {
      width: ${props => (props.menuOpen ? `100%` : `120%`)};
      top: ${props => (props.menuOpen ? `0` : `-10px`)};
      opacity: ${props => (props.menuOpen ? 0 : 1)};
      transition: ${({ menuOpen }) =>
        menuOpen ? 'var(--ham-before-active)' : 'var(--ham-before)'};
    }

    &:after {
      width: ${props => (props.menuOpen ? `100%` : `80%`)};
      bottom: ${props => (props.menuOpen ? `0` : `-10px`)};
      transform: rotate(${props => (props.menuOpen ? `-90deg` : `0`)});
      transition: ${({ menuOpen }) =>
        menuOpen ? 'var(--ham-after-active)' : 'var(--ham-after)'};
    }
  }
`;

const StyledSidebar = styled.aside`
  display: none;

  @media (max-width: 768px) {
    ${({ theme }) => theme.mixins.flexCenter};
    position: fixed;
    top: 0;
    bottom: 0;
    right: 0;
    padding: 90px 18px 30px;
    width: min(78vw, 380px);
    height: 100vh;
    outline: 0;
    background: rgba(255, 255, 255, 0.78);
    border-left: 1px solid rgba(255, 255, 255, 0.55);
    backdrop-filter: blur(22px);
    -webkit-backdrop-filter: blur(22px);
    box-shadow: -20px 0 40px rgba(0, 0, 0, 0.08);
    z-index: 19;
    transform: translateX(${props => (props.menuOpen ? 0 : 100)}%);
    visibility: ${props => (props.menuOpen ? 'visible' : 'hidden')};
    transition: var(--transition);
  }

  nav {
    ${({ theme }) => theme.mixins.flexBetween};
    width: 100%;
    flex-direction: column;
    color: var(--text-primary);
    font-family: var(--font-mono);
    text-align: left;
    align-items: stretch;
  }

  ol {
    padding: 0;
    margin: 0;
    list-style: none;
    width: 100%;
    display: grid;
    gap: 8px;

    li {
      position: relative;
      margin: 0;
      font-size: clamp(var(--fz-sm), 4vw, var(--fz-lg));
    }

    a {
      display: block;
      width: 100%;
      padding: 14px 16px;
      color: var(--text-primary);
      border-radius: 16px;
      background: rgba(255, 255, 255, 0.48);
      border: 1px solid var(--border-light);
      transition: var(--transition);
      text-decoration: none;
    }

    a:hover,
    a:focus-visible {
      color: var(--text-primary);
      background: rgba(255, 255, 255, 0.82);
      border-color: var(--border-medium);
      transform: translateY(-1px);
      outline: none;
    }
  }
`;

const Menu = () => {
  const [menuOpen, setMenuOpen] = useState(false);

  const toggleMenu = () => setMenuOpen(prev => !prev);

  const buttonRef = useRef(null);
  const navRef = useRef(null);
  const wrapperRef = useRef(null);

  let menuFocusables;
  let firstFocusableEl;
  let lastFocusableEl;

  const setFocusables = () => {
    if (!buttonRef.current) return;
    const navLinksEls = navRef.current
      ? Array.from(navRef.current.querySelectorAll('a'))
      : [];
    menuFocusables = [buttonRef.current, ...navLinksEls];
    firstFocusableEl = menuFocusables[0];
    lastFocusableEl = menuFocusables[menuFocusables.length - 1];
  };

  const handleBackwardTab = e => {
    if (document.activeElement === firstFocusableEl) {
      e.preventDefault();
      lastFocusableEl.focus();
    }
  };

  const handleForwardTab = e => {
    if (document.activeElement === lastFocusableEl) {
      e.preventDefault();
      firstFocusableEl.focus();
    }
  };

  const onKeyDown = e => {
    switch (e.key) {
      case KEY_CODES.ESCAPE:
      case KEY_CODES.ESCAPE_IE11:
        setMenuOpen(false);
        break;

      case KEY_CODES.TAB:
        if (!menuOpen) return;
        if (menuFocusables && menuFocusables.length === 1) {
          e.preventDefault();
          break;
        }
        if (e.shiftKey) {
          handleBackwardTab(e);
        } else {
          handleForwardTab(e);
        }
        break;

      default:
        break;
    }
  };

  const onResize = e => {
    if (e.currentTarget.innerWidth > 768) {
      setMenuOpen(false);
    }
  };

  useEffect(() => {
    setFocusables();

    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('resize', onResize);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('resize', onResize);
    };
  });

  useOnClickOutside(wrapperRef, () => setMenuOpen(false));

  return (
    <StyledMenu>
      <Helmet>
        <body className={menuOpen ? 'blur' : ''} />
      </Helmet>

      <div ref={wrapperRef}>
        <StyledHamburgerButton
          onClick={toggleMenu}
          menuOpen={menuOpen}
          ref={buttonRef}
          aria-label="Menu"
          aria-expanded={menuOpen}>
          <div className="ham-box">
            <div className="ham-box-inner" />
          </div>
        </StyledHamburgerButton>

        <StyledSidebar
          menuOpen={menuOpen}
          aria-hidden={!menuOpen}
          tabIndex={menuOpen ? 1 : -1}>
          <nav ref={navRef}>
            {navLinks && (
              <ol>
                {navLinks.map(({ url, name }, i) => (
                  <li key={`${name}-${i}`}>
                    <Link to={url} onClick={() => setMenuOpen(false)}>
                      {name}
                    </Link>
                  </li>
                ))}
              </ol>
            )}
          </nav>
        </StyledSidebar>
      </div>
    </StyledMenu>
  );
};

export default Menu;